import { YesNo } from "./models/enums";
import {
  RentalForm,
  RentalPremierForm,
} from "./models/rental/rental-form.model";
import {
  LoanCostOverride,
  RentalLolaFields,
  RentalOverrideOptions,
  RentalPremierQuote,
  RentalQuote,
  RentalUnderwriterFields,
  Scenario,
} from "./models/rental/rental.model";
import { FixAndFlipForm } from "./models/short-term/short-term-form.model";
import {
  BorrowerInfo,
  DealTerms,
  FixAndFlipOverrideOptions,
  FixAndFlipQuote,
  LoanCostOverrideSTF,
  LoanSummary,
  LoanTerm,
  ShortTermAdjustmentOptions,
  ShortTermLolaFields,
  ShortTermUnderwriterFields,
} from "./models/short-term/short-term.model";
import { QuoteType } from "./types/QuoteType";

export class QuoteUtils {
  public static normalizeOverrideValues<T extends object>(overrides: T) {
    const normalizedValues: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(overrides)) {
      if (value !== undefined && value !== "") {
        const val = key === "rateReductionOverride" ? +value : value;
        normalizedValues[key] = val;
      }
    }

    if (normalizedValues.rateReductionOverride === 0) {
      normalizedValues.rateReductionOverride = undefined;
    }

    return normalizedValues;
  }

  public static PrettyPrintQuoteType(quoteType: QuoteType): string {
    if (quoteType === "STF") {
      return "Short Term Finance";
    } else return quoteType;
  }

  public static FixAndFlipFormToQuote(
    formData: FixAndFlipForm
  ): Promise<FixAndFlipQuote> {
    const getKeyValue =
      <U extends keyof T, T extends object>(key: U) =>
      (obj: T) =>
        obj[key];

    return new Promise((res) => {
      const underwriterFields: ShortTermUnderwriterFields =
        new ShortTermUnderwriterFields(null);
      const lenderFinance: ShortTermLolaFields = new ShortTermLolaFields(null);
      const leverage: ShortTermAdjustmentOptions =
        new ShortTermAdjustmentOptions(null);
      const exceptionPricingOverride: FixAndFlipOverrideOptions =
        new FixAndFlipOverrideOptions(null);
      const loanSummary: LoanSummary = new LoanSummary();
      const loanTerm: LoanTerm = new LoanTerm();
      const dealTerms: DealTerms = new DealTerms();
      const borrowerInfo: BorrowerInfo = new BorrowerInfo();
      const loanCostOverride: LoanCostOverrideSTF = new LoanCostOverrideSTF(
        null
      );

      const objects = [
        underwriterFields,
        lenderFinance,
        exceptionPricingOverride,
        leverage,
        loanSummary,
        loanTerm,
        dealTerms,
        borrowerInfo,
        loanCostOverride,
      ];

      Object.keys(formData).forEach((key: any) => {
        for (let i = 0; i <= objects.length - 1; i++) {
          if (Object.prototype.hasOwnProperty.call(objects[i], key)) {
            const value = getKeyValue<keyof FixAndFlipForm, FixAndFlipForm>(
              key
            )(formData);
            const object = objects[i] as any;

            object[key] = value;
          }
        }
      });

      const fixAndFlipQuote: FixAndFlipQuote = {
        username: formData.username ?? "",
        quoteName: formData.quoteName,
        quoteType: formData.quoteType,
        overrideMaster: formData.overrideMaster,
        twoFourUnit: formData.twoFourUnit,
        sfaccountid: formData.sfaccountid,
        sfdealid: formData.sfdealid,
        campaignId: formData.campaignId,
        campaignName: formData.campaignName,
        campaignExpirationDate: formData.campaignExpirationDate,
        campaignIncentive: formData.campaignIncentive,
        campaignCode: formData.campaignCode,
        originationFeeAdjustment: formData.originationFeeAdjustment,
        lenderFinance,
        underwriterFields,
        leverage,
        borrowerInfo,
        dealTerms,
        exceptionPricingOverride:
          QuoteUtils.normalizeOverrideValues<FixAndFlipOverrideOptions>(
            exceptionPricingOverride
          ),
        loanSummary,
        loanTerm,
        loanCostOverride,
      };

      res(fixAndFlipQuote);
    });
  }

  public static RentalFormToPremierQuote(
    formData: RentalPremierForm
  ): Promise<RentalPremierQuote> {
    return new Promise((res) => {
      const rentalPremierQuote: RentalPremierQuote = {
        quoteName: formData.quoteName,
        brokerFee: formData.brokerFee,
        borrowerIsReferral: formData.borrowerIsReferral,
        appraisedVal1: formData.appraisedVal1,
        creditScore: formData.creditScore,
        submissionType: formData.submissionType,
        annualTaxes: formData.annualTaxes,
        propertiesNY: formData.propertiesNY,
        annualInsurance: formData.annualInsurance,
        refiL1cFixNFlip: formData.refiL1cFixNFlip,
        buyUpDownOption: formData.buyUpDownOption,
        username: formData.username,
        sfaccountid: formData.sfaccountid,
        sfdealid: formData.sfdealid,
        numOfProperties: formData.numOfProperties,
        overrideMaster: formData.overrideMaster,
        annualFloodInsurance: formData.annualFloodInsurance,
        borrowingEntity: formData.borrowingEntity,
        mortgagePayoffs: formData.mortgagePayoffs,
        quoteType: "Premier",
        appraisedVal3: formData.appraisedVal3,
        mortgagePayoffs2: formData.mortgagePayoffs2,
        originalPurchasePrice: formData.originalPurchasePrice,
        originalPurchaseDate: formData.originalPurchaseDate,
        targetCloseDate: formData.targetCloseDate,
        totalCosts1: formData.totalCosts1,
        monthlyRent: formData.monthlyRent,
        sreoPropertyNumber: formData.sreoPropertyNumber,
        productType: formData.productType,
        purchasePrice: formData.purchasePrice,
        prepaymentPenalty: formData.prepaymentPenalty,
        borrowerNetWorth: formData.borrowerNetWorth,
        yieldSpread: parseFloat(formData.yieldSpread?.toString()),
        annualHOA: formData.annualHOA,
        base: {
          condo: formData.condo,
          fixToRent: formData.fixToRent,
          propertyVal: formData.propertyVal,
        },
        buyUpDownPercentage: formData.buyUpDownPercentage,
        brokerProcessingFee: formData.brokerProcessingFee,
        appraisalFee: formData.appraisalFee,
        usCitizen: formData.usCitizen,
        isTier5Borrower: formData.isTier5Borrower,
        premier: {
          collateral: formData.collateral,
          collateralCondos: formData.collateralCondos,
          index: formData.index,
          openPrepayOption: formData.openPrepayOption,
          propertyManagementFee: formData.propertyManagementFee,
        },
      };

      res(rentalPremierQuote);
    });
  }

  public static RentalFormToQuote(formData: RentalForm): Promise<RentalQuote> {
    return new Promise((res) => {
      const exceptionPricingOverride: RentalOverrideOptions =
        new RentalOverrideOptions(formData);
      const underwriterFields: RentalUnderwriterFields =
        new RentalUnderwriterFields(formData);
      const lenderFinance: RentalLolaFields = new RentalLolaFields(formData);
      const loanCostOverride: LoanCostOverride = new LoanCostOverride(formData);
      const rentalQuote: RentalQuote = {
        quoteName: formData.quoteName,
        brokerFee: formData.brokerFee,
        borrowerIsReferral: formData.borrowerIsReferral,
        appraisedVal1: formData.appraisedVal1,
        creditScore: formData.creditScore,
        submissionType: formData.submissionType,
        annualTaxes: formData.annualTaxes,
        propertiesNY: formData.propertiesNY,
        state: formData.state,
        annualInsurance: formData.annualInsurance,
        refiL1cFixNFlip: formData.refiL1cFixNFlip,
        buyUpDownOption: formData.buyUpDownOption,
        username: formData.username,
        numOfProperties: formData.numOfProperties,
        overrideMaster: formData.overrideMaster,
        annualFloodInsurance: formData.annualFloodInsurance,
        borrowingEntity: formData.borrowingEntity,
        mortgagePayoffs: formData.mortgagePayoffs,
        quoteType: "Rental",
        appraisedVal3: formData.appraisedVal3,
        mortgagePayoffs2: formData.mortgagePayoffs2,
        originalPurchasePrice: formData.originalPurchasePrice,
        originalPurchaseDate: formData.originalPurchaseDate,
        targetCloseDate: formData.targetCloseDate,
        totalCosts1: formData.totalCosts1,
        renovationCostsAfterPurchase: formData.renovationCostsAfterPurchase,
        monthlyRent: formData.monthlyRent,
        sreoPropertyNumber: formData.sreoPropertyNumber,
        wholesalePartner: formData.wholesalePartner,
        productType: formData.productType,
        purchasePrice: formData.purchasePrice,
        prepaymentPenalty: formData.prepaymentPenalty,
        borrowerNetWorth: formData.borrowerNetWorth,
        yieldSpread: parseFloat(formData.yieldSpread?.toString()),
        annualHOA: formData.annualHOA,
        base: {
          condo: formData.condo,
          fixToRent: formData.fixToRent,
          propertyVal: formData.propertyVal,
        },
        buyUpDownPercentage: formData.buyUpDownPercentage,
        brokerProcessingFee: formData.brokerProcessingFee,
        appraisalFee: formData.appraisalFee,
        usCitizen: formData.usCitizen,
        isTier5Borrower: formData.isTier5Borrower,
        sfaccountid: formData.sfaccountid,
        sfdealid: formData.sfdealid,
        campaignId: formData.campaignId,
        campaignCode: formData.campaignCode,
        campaignExpirationDate: formData.campaignExpirationDate,
        campaignIncentive: formData.campaignIncentive,
        campaignName: formData.campaignName,
        isZeroOrigFee: formData.isZeroOrigFee,
        seasonalRental: formData.seasonalRental,
        fiveNineUnit: formData.fiveNineUnit,
        twoFourUnit: formData.twoFourUnit,
        vacantProperty: formData.vacantProperty,
        zip: formData.zip,
        propertyAddress: formData.propertyAddress,
        submittedDate: formData.submittedDate,
        city: formData.city,
        exceptionPricingOverride:
          QuoteUtils.normalizeOverrideValues<RentalOverrideOptions>(
            exceptionPricingOverride
          ),
        underwriterFields,
        lenderFinance,
        loanCostOverride,
        originationFeeAdjustment: formData.originationFeeAdjustment,
        documentPrepFee: formData.documentPrepFee
      };

      res(rentalQuote);
    });
  }

  public static GenerateRentalQuoteScenarios(): Scenario[] {
    return [
      {
        scenarioId: 1,
        included: YesNo.Yes,
        preferred: YesNo.Yes,
        ltvPercentage: 75,
      },
      {
        scenarioId: 2,
        included: YesNo.Yes,
        preferred: YesNo.No,
        ltvPercentage: 70,
      },
      {
        scenarioId: 3,
        included: YesNo.Yes,
        preferred: YesNo.No,
        ltvPercentage: 65,
      },
    ];
  }
}
